import { useMemo } from 'react';
import {
  Publishable,
  PublishableTestimonial,
} from '~/types/models/publishable';
import intersect from 'lodash/intersection';
import diff from 'lodash/difference';
import shuffle from 'lodash/shuffle';
import { DisplayOrder } from './Containers/Sidebar/Components/Sections/Select';

export interface UseOrderedTestimonialsOptions {
  data?: PublishableTestimonial[];
  order?: string[];
  displayOrder?: DisplayOrder;
}

function getOrderedKeys(
  keys: string[],
  {
    displayOrder,
    order,
  }: Pick<UseOrderedTestimonialsOptions, 'order' | 'displayOrder'>
): string[] {
  if (displayOrder === DisplayOrder.Manual) {
    const orderedKeys = order != null ? intersect(order, keys) : keys;

    if (orderedKeys.length !== keys.length) {
      orderedKeys.push(...diff(keys, orderedKeys));
    }

    return orderedKeys;
  }

  return shuffle(keys);
}

export function usePublishableMap(
  publish: Publishable,
  data?: PublishableTestimonial[]
) {
  return useMemo<Record<string, PublishableTestimonial>>(
    () =>
      (data ?? publish.publishable_resources)?.reduce(
        (result, pub) => ({
          ...result,
          [pub.uuid]: pub,
        }),
        {}
      ) ?? {},
    [publish.publishable_resources, data]
  );
}

export function useOrderedTestimonials(
  publish: Publishable,
  options?: Partial<UseOrderedTestimonialsOptions>
) {
  const settings = useMemo<UseOrderedTestimonialsOptions>(
    () => ({
      data: options?.data,
      order: options?.order ?? publish.metadata?.order,
      displayOrder: options?.displayOrder ?? publish.metadata?.display_order,
    }),
    [options, publish.metadata]
  );

  const publishableMap = usePublishableMap(publish, settings.data);

  return useMemo(() => {
    const keys = Object.keys(publishableMap);

    const orderedKeys = getOrderedKeys(keys, settings);

    return orderedKeys.map((uuid) => publishableMap[uuid]);
  }, [publishableMap, settings]);
}
